import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  School as SchoolIcon,
  Gavel as LegalIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  GetApp as DownloadIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

interface GlossaryTerm {
  term: string;
  definition: string;
  category: 'General' | 'Legal' | 'Financial' | 'NJ Specific' | 'Title';
  relatedTerms?: string[];
  example?: string;
}

const glossaryTerms: GlossaryTerm[] = [
  {
    term: 'Abstract of Title',
    definition: 'A condensed history of the title to real estate, including a summary of all recorded documents affecting the property.',
    category: 'Title',
    relatedTerms: ['Chain of Title', 'Title Search'],
    example: 'The abstract showed clear title back to 1952 when the original subdivision was created.',
  },
  {
    term: 'ALTA Policy',
    definition: 'American Land Title Association standardized title insurance policy forms used throughout New Jersey.',
    category: 'Title',
    relatedTerms: ['Title Insurance', 'Owner\'s Policy'],
  },
  {
    term: 'Assessment',
    definition: 'The value placed on real estate by a municipality for taxation purposes. In NJ, reassessments occur periodically.',
    category: 'NJ Specific',
    example: 'Woodbridge Township conducted a township-wide reassessment in 2023.',
  },
  {
    term: 'Chain of Title',
    definition: 'The chronological sequence of conveyances, encumbrances, and other title documents affecting a particular parcel of land.',
    category: 'Title',
    relatedTerms: ['Abstract of Title', 'Title Search', 'Deed'],
  },
  {
    term: 'Closing Disclosure (CD)',
    definition: 'Federal form that provides final details about the mortgage loan, including loan terms, projected monthly payments, and closing costs.',
    category: 'Financial',
    relatedTerms: ['TRID', 'Loan Estimate'],
  },
  {
    term: 'Deed',
    definition: 'A written instrument that conveys title to real property from one party to another.',
    category: 'Legal',
    relatedTerms: ['Warranty Deed', 'Quitclaim Deed', 'Bargain and Sale Deed'],
  },
  {
    term: 'Easement',
    definition: 'A right to use another person\'s land for a specific purpose, such as utilities or access.',
    category: 'Legal',
    example: 'The utility company has an easement across the back of the property for power lines.',
  },
  {
    term: 'Encumbrance',
    definition: 'Any claim, lien, charge, or liability attached to and binding real property.',
    category: 'Legal',
    relatedTerms: ['Lien', 'Mortgage', 'Easement'],
  },
  {
    term: 'Escrow',
    definition: 'Funds held by a neutral third party until certain conditions are met. In NJ, title companies often serve as escrow agents.',
    category: 'Financial',
    relatedTerms: ['Closing', 'Title Company'],
  },
  {
    term: 'Fee Simple',
    definition: 'The most complete form of ownership of real estate, giving the owner absolute title.',
    category: 'Legal',
    relatedTerms: ['Title', 'Ownership'],
  },
  {
    term: 'Homestead Exemption',
    definition: 'New Jersey property tax exemption available to eligible homeowners, providing annual tax savings.',
    category: 'NJ Specific',
    example: 'Senior citizens and disabled veterans may qualify for additional homestead exemptions.',
  },
  {
    term: 'Judgment',
    definition: 'A court decision that creates a lien against the debtor\'s real estate until the debt is satisfied.',
    category: 'Legal',
    relatedTerms: ['Lien', 'Encumbrance'],
  },
  {
    term: 'Lien',
    definition: 'A legal claim against property that must be satisfied before the property can be sold or refinanced.',
    category: 'Legal',
    relatedTerms: ['Tax Lien', 'Mechanic\'s Lien', 'Judgment'],
  },
  {
    term: 'Lis Pendens',
    definition: 'A recorded notice that a lawsuit is pending affecting the title to the property.',
    category: 'Legal',
    relatedTerms: ['Litigation', 'Title Defect'],
  },
  {
    term: 'Mechanic\'s Lien',
    definition: 'A statutory lien in favor of contractors, subcontractors, or suppliers who have provided labor or materials for property improvements.',
    category: 'NJ Specific',
    example: 'In NJ, contractors have 90 days from last work to file a mechanic\'s lien.',
  },
  {
    term: 'Mortgage',
    definition: 'A security instrument that pledges real estate as collateral for a loan.',
    category: 'Financial',
    relatedTerms: ['Note', 'Lien', 'Deed of Trust'],
  },
  {
    term: 'NJ Realty Transfer Fee',
    definition: 'State fee of $5.15 per $1,000 of consideration paid when transferring real estate in New Jersey.',
    category: 'NJ Specific',
    example: 'On a $400,000 sale, the NJ Realty Transfer Fee would be $2,060.',
  },
  {
    term: 'Owner\'s Policy',
    definition: 'Title insurance policy that protects the property owner against title defects.',
    category: 'Title',
    relatedTerms: ['Title Insurance', 'Lender\'s Policy'],
  },
  {
    term: 'Power of Attorney',
    definition: 'A legal document authorizing someone to act on behalf of another person in legal or financial matters.',
    category: 'Legal',
  },
  {
    term: 'Quitclaim Deed',
    definition: 'A deed that transfers only the interest the grantor has in the property, with no warranties.',
    category: 'Legal',
    relatedTerms: ['Deed', 'Warranty Deed'],
  },
  {
    term: 'Recording',
    definition: 'The process of filing documents in public records, giving constructive notice to the world.',
    category: 'Legal',
    example: 'Deeds must be recorded at the county clerk\'s office where the property is located.',
  },
  {
    term: 'RESPA',
    definition: 'Real Estate Settlement Procedures Act - federal law requiring disclosure of settlement costs.',
    category: 'Legal',
    relatedTerms: ['Closing Disclosure', 'Settlement Statement'],
  },
  {
    term: 'Survey',
    definition: 'A drawing showing the exact legal boundaries of a property, including improvements and easements.',
    category: 'General',
    relatedTerms: ['Boundary', 'Easement', 'Encroachment'],
  },
  {
    term: 'Tax Certificate',
    definition: 'Document from the municipal tax collector showing current tax status of the property.',
    category: 'NJ Specific',
    example: 'Most NJ municipalities require a current tax certificate for closing.',
  },
  {
    term: 'Title Insurance',
    definition: 'Insurance that protects against financial loss from defects in title to real estate.',
    category: 'Title',
    relatedTerms: ['Owner\'s Policy', 'Lender\'s Policy', 'Title Search'],
  },
  {
    term: 'Title Search',
    definition: 'An examination of public records to determine the history of ownership and encumbrances affecting a property.',
    category: 'Title',
    relatedTerms: ['Abstract of Title', 'Chain of Title'],
  },
  {
    term: 'TRID',
    definition: 'TILA-RESPA Integrated Disclosure rule requiring specific timing and forms for mortgage disclosures.',
    category: 'Legal',
    relatedTerms: ['Closing Disclosure', 'Loan Estimate'],
  },
  {
    term: 'Warranty Deed',
    definition: 'A deed that guarantees the grantor has clear title and will defend against all claims.',
    category: 'Legal',
    relatedTerms: ['Deed', 'Title', 'Quitclaim Deed'],
  },
  {
    term: 'Zoning',
    definition: 'Municipal regulations controlling the use of land and buildings within designated areas.',
    category: 'General',
    example: 'The property is zoned R-1 for single-family residential use.',
  },
];

const categories = ['All', 'General', 'Legal', 'Financial', 'NJ Specific', 'Title'];

const NJRealEstateGlossary: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [expandedTerm, setExpandedTerm] = useState<string | false>(false);

  const filteredTerms = useMemo(() => {
    return glossaryTerms.filter(term => {
      const matchesSearch = term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          term.definition.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || term.category === selectedCategory;
      return matchesSearch && matchesCategory;
    }).sort((a, b) => a.term.localeCompare(b.term));
  }, [searchTerm, selectedCategory]);

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedTerm(isExpanded ? panel : false);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Legal': return <LegalIcon sx={{ fontSize: 18 }} />;
      case 'Title': return <BusinessIcon sx={{ fontSize: 18 }} />;
      case 'NJ Specific': return <HomeIcon sx={{ fontSize: 18 }} />;
      default: return <SchoolIcon sx={{ fontSize: 18 }} />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Legal': return '#dc2626';
      case 'Financial': return '#059669';
      case 'Title': return '#1e3a8a';
      case 'NJ Specific': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  return (
    <>
      <Helmet>
        <title>New Jersey Real Estate Glossary | MAD Title Agency - Essential Terms & Definitions</title>
        <meta
          name="description"
          content="Comprehensive glossary of New Jersey real estate terms. Essential definitions for homebuyers, sellers, and professionals. Title insurance, legal terms, and NJ-specific requirements."
        />
        <meta
          name="keywords"
          content="New Jersey real estate glossary, NJ property terms, title insurance definitions, real estate legal terms, homebuyer education, Middlesex County real estate"
        />
      </Helmet>

      {/* Breadcrumb */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link> →{' '}
            <Link to="/resources" style={{ textDecoration: 'none', color: 'inherit' }}>Resources</Link> →{' '}
            NJ Real Estate Glossary
          </Typography>
        </Container>
      </Box>

      {/* Header */}
      <Box sx={{ backgroundColor: '#1e3a8a', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="Reference Guide"
                sx={{
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  mb: 2,
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                New Jersey Real Estate Glossary
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                Essential terms and definitions for NJ property transactions
              </Typography>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: '#f59e0b',
                  '&:hover': { backgroundColor: '#d97706' },
                }}
              >
                Download Glossary PDF
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <SchoolIcon sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {glossaryTerms.length} Terms
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Comprehensive Reference
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Search and Filter */}
        <Box sx={{ mb: 6 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search terms or definitions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Filter by Category:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {categories.map((category) => (
                  <Chip
                    key={category}
                    label={category}
                    variant={selectedCategory === category ? 'filled' : 'outlined'}
                    color={selectedCategory === category ? 'primary' : 'default'}
                    onClick={() => setSelectedCategory(category)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>

          {searchTerm && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" color="text.secondary">
                {filteredTerms.length} term{filteredTerms.length !== 1 ? 's' : ''} found for "{searchTerm}"
              </Typography>
            </Box>
          )}
        </Box>

        {/* Introduction */}
        <Box sx={{ mb: 6 }}>
          <Alert severity="info" sx={{ mb: 4 }}>
            <Typography variant="body2">
              This glossary focuses on terms commonly used in New Jersey real estate transactions, 
              with special attention to local laws, procedures, and requirements specific to 
              Middlesex County and surrounding areas.
            </Typography>
          </Alert>
        </Box>

        {/* Terms List */}
        <Box sx={{ mb: 8 }}>
          {filteredTerms.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                No terms found matching your search criteria.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('All');
                }}
              >
                Clear Search
              </Button>
            </Box>
          ) : (
            filteredTerms.map((term, index) => (
              <Accordion
                key={term.term}
                expanded={expandedTerm === term.term}
                onChange={handleAccordionChange(term.term)}
                sx={{ mb: 1, border: '1px solid #e2e8f0' }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                      {getCategoryIcon(term.category)}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {term.term}
                      </Typography>
                    </Box>
                    <Chip
                      label={term.category}
                      size="small"
                      sx={{
                        backgroundColor: getCategoryColor(term.category),
                        color: 'white',
                        mr: 2,
                      }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ pt: 2 }}>
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                      {term.definition}
                    </Typography>
                    
                    {term.example && (
                      <Box sx={{ backgroundColor: '#f0fdf4', p: 3, borderRadius: 1, mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#16a34a', mb: 1 }}>
                          Example:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {term.example}
                        </Typography>
                      </Box>
                    )}

                    {term.relatedTerms && term.relatedTerms.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Related Terms:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {term.relatedTerms.map((relatedTerm, relatedIndex) => (
                            <Chip
                              key={relatedIndex}
                              label={relatedTerm}
                              size="small"
                              variant="outlined"
                              onClick={() => setSearchTerm(relatedTerm)}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Box>

        {/* Additional Resources */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Related Resources
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <HomeIcon sx={{ fontSize: 48, color: '#1e3a8a', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  First-Time Homebuyer Guide
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Comprehensive guide for first-time buyers in Middlesex County.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/first-time-buyer-middlesex"
                  fullWidth
                >
                  Learn More
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <BusinessIcon sx={{ fontSize: 48, color: '#1e3a8a', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Title Insurance Guide
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Understanding title insurance in New Jersey.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/title-insurance-woodbridge"
                  fullWidth
                >
                  Learn More
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <LegalIcon sx={{ fontSize: 48, color: '#1e3a8a', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Closing Process
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Step-by-step guide to the NJ closing process.
                </Typography>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/resources/escrow-process-explained"
                  fullWidth
                >
                  Learn More
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600, color: '#64748b' }}>
              Need Help With Your Transaction?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
              Our experienced team can guide you through every step of your New Jersey real estate transaction.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Contact Our Experts
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/resources/closing-cost-calculator"
                sx={{
                  borderColor: '#1e3a8a',
                  color: '#1e3a8a',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Calculate Closing Costs
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default NJRealEstateGlossary; 